import { useQuery } from '@apollo/client';
import type { SlackAccount } from '@coursera-web/slack-account-link';

import type {
  ExternalSlackTeamData,
  ExternalSlackTeamInformation,
  SlackAccountData,
  SlackDegree,
  SlackMyAccountData,
} from 'bundles/third-party-auth/components/AccountLink/types';
import SlackDegreesQuery from 'bundles/third-party-auth/queries/SlackDegreesQuery.graphql';
import {
  ExternalSlackTeamInformationQuery,
  SlackMyAccountsQuery,
} from 'bundles/third-party-auth/queries/SlackRestQueries';
import type {
  SlackDegreesQuery as SlackDegreesQueryData,
  SlackDegreesQueryVariables,
} from 'bundles/third-party-auth/queries/__generated__/SlackDegreesQuery';

export const transformData = (
  accounts: SlackAccount[],
  degreesByDegreeId: Record<string, SlackDegree>,
  slackTeamsByDegreeId: Record<string, ExternalSlackTeamInformation>
): SlackAccountData[] => {
  return accounts.map((account) => {
    const degree = degreesByDegreeId[account.degreeId];
    const slackTeamInfo = slackTeamsByDegreeId[account.degreeId];
    return {
      ...account,
      degreeName: degree?.name || '',
      partnerName: degree?.partners[0]?.name || '',
      slackTeamDomainName: slackTeamInfo?.slackTeam.slackTeamDomain,
    };
  });
};

export const useSlackAccounts = (): { slackAccounts: SlackAccountData[]; loading: boolean; error: boolean } => {
  const {
    data: myAccountsData,
    loading: myAccountsLoading,
    error: myAccountsError,
  } = useQuery<SlackMyAccountData>(SlackMyAccountsQuery);

  const accounts = myAccountsData?.SlackAuthV1Resource?.elements ?? [];
  const degreeIds = accounts.map((account) => account.degreeId);

  const {
    data: degreesData,
    loading: degreesLoading,
    error: degreesError,
  } = useQuery<SlackDegreesQueryData, SlackDegreesQueryVariables>(SlackDegreesQuery, {
    variables: { ids: degreeIds },
    skip: degreeIds.length === 0,
    errorPolicy: 'all',
    context: { clientName: 'gatewayGql' },
  });

  const {
    data: slackTeamsData,
    loading: slackTeamsLoading,
    error: slackTeamsError,
  } = useQuery<ExternalSlackTeamData>(ExternalSlackTeamInformationQuery, {
    variables: { ids: degreeIds.join(',') },
    skip: degreeIds.length === 0,
  });

  const degreesByDegreeId: Record<string, SlackDegree> = Object.fromEntries(
    (degreesData?.Degree?.queryByIds ?? []).map((d) => [d.id, d])
  );
  const slackTeamsByDegreeId = Object.fromEntries(
    (slackTeamsData?.ExternalSlackTeamInformationV1Resource?.elements ?? []).map((t) => [t.degreeId, t])
  );

  const merged = transformData(accounts, degreesByDegreeId, slackTeamsByDegreeId);
  const slackAccounts = merged.filter((account) => !!account.slackTeamDomainName);

  return {
    slackAccounts,
    loading: myAccountsLoading || degreesLoading || slackTeamsLoading,
    error: !!(myAccountsError || degreesError || slackTeamsError),
  };
};

export default useSlackAccounts;
